/**
 * NotificationCenter — the header bell + persistent issue-log popover.
 *
 * MERIDIAN §Notifications → "Notification Center (persistent)": opened from a
 * bell in the header's right cluster, carrying an unread-count badge (capped at
 * "9+"). Opens as an opaque popover anchored beneath the bell, above the sidebar
 * and below modals. Reverse-chronological, sectioned Today → Earlier → Older,
 * with a filter chip row (All · Unread · by source), read/unread keylines, a
 * "Mark all read" action, and a calm empty state.
 *
 * The bell lives in the InstrumentBezel top spine; the popover portals to <body>
 * and is anchored with `position: fixed` so it can never be clipped by an
 * ancestor's overflow (see [[collapsed-flyout-clipped-by-overflow]]).
 */

import { useEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { useNavigate } from 'react-router-dom';
import {
  useNotificationCenterStore,
  selectUnreadCount,
  selectSortedNotifications,
  type CenterNotification,
  type NotificationSource,
} from '../../store/notificationCenterStore';
import './NotificationCenter.css';

// ─── Icons ───────────────────────────────────────────────────────────────────

const BellGlyph = () => (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" aria-hidden="true">
    <path
      d="M18 8A6 6 0 0 0 6 8c0 7-3 9-3 9h18s-3-2-3-9M13.73 21a2 2 0 0 1-3.46 0"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

// Source glyphs (16px, currentColor). Kept small + filled/stroked to read at a glance.
const SOURCE_ICON: Record<NotificationSource, string> = {
  network: 'M12 2a10 10 0 1 0 0 20 10 10 0 0 0 0-20zm0 3c1.7 2 2.5 4.5 2.5 7s-.8 5-2.5 7c-1.7-2-2.5-4.5-2.5-7s.8-5 2.5-7zM3.5 9h17M3.5 15h17',
  favorite: 'M12 17.3l-6.2 3.7 1.6-7L2 9.2l7.1-.6L12 2l2.9 6.6 7.1.6-5.4 4.8 1.6 7z',
  qc: 'M9 12l2 2 4-4m6 2a9 9 0 1 1-18 0 9 9 0 0 1 18 0z',
  sync: 'M21 2v6h-6M3 12a9 9 0 0 1 15-6.7L21 8M3 22v-6h6M21 12a9 9 0 0 1-15 6.7L3 16',
  ingestion: 'M12 2v13m0 0l-4-4m4 4l4-4M4 17v2a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2v-2',
  system: 'M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm1 15h-2v-6h2v6zm0-8h-2V7h2v2z',
};

const SOURCE_LABEL: Record<NotificationSource, string> = {
  network: 'Network',
  favorite: 'Favorites',
  qc: 'QC',
  sync: 'Sync',
  ingestion: 'Ingestion',
  system: 'System',
};

// ─── Time helpers ─────────────────────────────────────────────────────────────

const HOUR = 3_600_000;
const DAY = 24 * HOUR;

/** Compact relative age from an epoch-ms timestamp: "just now", "5m", "3h", "2d". */
function relativeFromNow(ts: number, now: number): string {
  const diff = Math.max(0, now - ts);
  if (diff < 60_000) return 'just now';
  if (diff < HOUR) return `${Math.floor(diff / 60_000)}m`;
  if (diff < DAY) return `${Math.floor(diff / HOUR)}h`;
  if (diff < 30 * DAY) return `${Math.floor(diff / DAY)}d`;
  return `${Math.floor(diff / (30 * DAY))}mo`;
}

type Bucket = 'today' | 'earlier' | 'older';
const BUCKET_LABEL: Record<Bucket, string> = { today: 'Today', earlier: 'Earlier', older: 'Older' };

/** Today (same calendar day) → Earlier (last 7 days) → Older. */
function bucketFor(ts: number, now: number): Bucket {
  const start = new Date(now);
  start.setHours(0, 0, 0, 0);
  if (ts >= start.getTime()) return 'today';
  if (ts >= now - 7 * DAY) return 'earlier';
  return 'older';
}

// ─── Component ─────────────────────────────────────────────────────────────────

type FilterChip = 'all' | 'unread' | NotificationSource;

export const NotificationCenter = () => {
  const navigate = useNavigate();
  const notifications = useNotificationCenterStore(selectSortedNotifications);
  const unread = useNotificationCenterStore(selectUnreadCount);
  const markRead = useNotificationCenterStore((s) => s.markRead);
  const markAllRead = useNotificationCenterStore((s) => s.markAllRead);
  const remove = useNotificationCenterStore((s) => s.remove);

  const [open, setOpen] = useState(false);
  const [filter, setFilter] = useState<FilterChip>('all');
  const rootRef = useRef<HTMLDivElement | null>(null);
  const panelRef = useRef<HTMLDivElement | null>(null);

  // Close on outside click / Escape. The panel is portaled, so the outside test
  // must exclude BOTH the bell root and the portaled panel.
  useEffect(() => {
    if (!open) return;
    const onDown = (e: MouseEvent) => {
      const t = e.target as Node;
      if (rootRef.current?.contains(t)) return;
      if (panelRef.current?.contains(t)) return;
      setOpen(false);
    };
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') setOpen(false); };
    document.addEventListener('mousedown', onDown);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onDown);
      document.removeEventListener('keydown', onKey);
    };
  }, [open]);

  // Source chips are derived from what's actually in the log (stable order).
  const sourcesPresent = useMemo(() => {
    const order: NotificationSource[] = ['network', 'favorite', 'qc', 'sync', 'ingestion', 'system'];
    const present = new Set(notifications.map((n) => n.source));
    return order.filter((s) => present.has(s));
  }, [notifications]);

  const filtered = useMemo(() => {
    if (filter === 'all') return notifications;
    if (filter === 'unread') return notifications.filter((n) => !n.read);
    return notifications.filter((n) => n.source === filter);
  }, [notifications, filter]);

  // Group filtered entries into Today / Earlier / Older, preserving sort order.
  const groups = useMemo(() => {
    const now = Date.now();
    const out: { bucket: Bucket; items: CenterNotification[] }[] = [];
    const byBucket = new Map<Bucket, CenterNotification[]>();
    for (const n of filtered) {
      const b = bucketFor(n.updatedAt, now);
      const arr = byBucket.get(b) ?? [];
      arr.push(n);
      byBucket.set(b, arr);
    }
    (['today', 'earlier', 'older'] as Bucket[]).forEach((b) => {
      const items = byBucket.get(b);
      if (items && items.length) out.push({ bucket: b, items });
    });
    return out;
  }, [filtered]);

  const now = Date.now();
  const badge = unread > 9 ? '9+' : String(unread);

  const activateRow = (n: CenterNotification) => {
    markRead(n.id);
    if (n.href) {
      setOpen(false);
      navigate(n.href);
    }
  };

  return (
    <div className="ib-notif" ref={rootRef}>
      <button
        type="button"
        className="ib-notif__bell"
        onClick={() => setOpen((v) => !v)}
        aria-haspopup="dialog"
        aria-expanded={open}
        aria-label={unread > 0 ? `Notifications, ${unread} unread` : 'Notifications'}
        title="Notifications"
      >
        <BellGlyph />
        {unread > 0 && <span className="ib-notif__badge" aria-hidden="true">{badge}</span>}
      </button>

      {open && createPortal(
        <div
          className="notif-center"
          role="dialog"
          aria-label="Notification center"
          ref={panelRef}
        >
          <div className="notif-center__head">
            <span className="notif-center__title">Notifications</span>
            {unread > 0 && (
              <button
                type="button"
                className="notif-center__markall"
                onClick={markAllRead}
              >
                Mark all read
              </button>
            )}
          </div>

          {notifications.length > 0 && (
            <div className="notif-center__filters" role="tablist" aria-label="Filter notifications">
              <FilterChipBtn label="All" active={filter === 'all'} onClick={() => setFilter('all')} />
              <FilterChipBtn label="Unread" active={filter === 'unread'} onClick={() => setFilter('unread')} count={unread} />
              {sourcesPresent.map((s) => (
                <FilterChipBtn key={s} label={SOURCE_LABEL[s]} active={filter === s} onClick={() => setFilter(s)} />
              ))}
            </div>
          )}

          <div className="notif-center__list" role="feed" aria-busy="false">
            {groups.length === 0 ? (
              <p className="notif-center__empty">You’re all caught up</p>
            ) : (
              groups.map(({ bucket, items }) => (
                <section key={bucket} className="notif-center__group">
                  <h3 className="notif-center__group-title">{BUCKET_LABEL[bucket]}</h3>
                  {items.map((n) => (
                    <article
                      key={n.id}
                      className={`notif-row notif-row--${n.severity}${n.read ? '' : ' notif-row--unread'}`}
                      role="article"
                      tabIndex={0}
                      onClick={() => activateRow(n)}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); activateRow(n); }
                      }}
                    >
                      <svg className="notif-row__icon" width="16" height="16" viewBox="0 0 24 24"
                        fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                        <path d={SOURCE_ICON[n.source]} />
                      </svg>
                      <div className="notif-row__body">
                        <div className="notif-row__top">
                          <span className="notif-row__title">{n.title}</span>
                          <time className="notif-row__time" dateTime={new Date(n.updatedAt).toISOString()}>
                            {relativeFromNow(n.updatedAt, now)}
                          </time>
                        </div>
                        <span className="notif-row__message">{n.message}</span>
                      </div>
                      <button
                        type="button"
                        className="notif-row__dismiss"
                        onClick={(e) => { e.stopPropagation(); remove(n.id); }}
                        aria-label={`Dismiss ${n.title} notification`}
                      >
                        <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true">
                          <path d="M18 6L6 18M6 6l12 12" />
                        </svg>
                      </button>
                    </article>
                  ))}
                </section>
              ))
            )}
          </div>
        </div>,
        document.body,
      )}
    </div>
  );
};

interface FilterChipBtnProps {
  label: string;
  active: boolean;
  onClick: () => void;
  count?: number;
}
const FilterChipBtn = ({ label, active, onClick, count }: FilterChipBtnProps) => (
  <button
    type="button"
    role="tab"
    aria-selected={active}
    className={`notif-chip${active ? ' notif-chip--active' : ''}`}
    onClick={onClick}
  >
    {label}
    {count != null && count > 0 && <span className="notif-chip__count">{count}</span>}
  </button>
);
